import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Icon } from '../icons'
import { V3_SECONDARY_BUTTON_CLS } from './controlStyles'

interface Tab { key: string; label: string; content: ReactNode; guidanceFields?: string[] | undefined }

/** Shared record chrome. Hosts supply action bindings; content comes exclusively from the view renderer. */
export function RecordWorkspace({ title, header, actions, toolbar, navigation, general, tabs, guidanceCompany, guidanceNew, guidanceGeneral }: {
    title?: string | undefined
    header: ReactNode
    actions?: ReactNode
    toolbar?: ReactNode
    navigation?: ReactNode
    general: ReactNode
    tabs: Tab[]
    guidanceCompany?: string | undefined
    guidanceNew?: boolean
    guidanceGeneral?: string[]
}) {
    const id = useId()
    const [selected, setSelected] = useState('general')
    const [visited, setVisited] = useState<Set<string>>(() => new Set(['general']))
    const [menuOpen, setMenuOpen] = useState(false)
    const [capacity, setCapacity] = useState(Infinity)
    const strip = useRef<HTMLDivElement>(null)
    const measure = useRef<HTMLDivElement>(null)
    const menu = useRef<HTMLDivElement>(null)
    const more = useRef<HTMLButtonElement>(null)
    const all: Tab[] = [{ key: 'general', label: 'General', content: general, guidanceFields: guidanceGeneral }, ...tabs]
    const active = all.some(tab => tab.key === selected) ? selected : 'general'
    const labelsKey = all.map(tab => `${tab.key}:${tab.label}`).join('|')

    useEffect(() => {
        const resize = () => {
            if (!strip.current || !measure.current) return
            const width = strip.current.clientWidth
            if (!width) return // SSR/jsdom: the unmeasured strip remains usable.
            const widths = Array.from(measure.current.children, child => child.getBoundingClientRect().width)
            const total = widths.reduce((sum, item) => sum + item, 0)
            if (total <= width) { setCapacity(Infinity); return }
            let used = 0, count = 0
            for (const item of widths) {
                if (used + item + 100 > width) break
                used += item
                count++
            }
            setCapacity(Math.max(1, count))
        }
        resize()
        if (typeof ResizeObserver === 'undefined' || !strip.current) return
        const observer = new ResizeObserver(resize)
        observer.observe(strip.current)
        return () => observer.disconnect()
    }, [labelsKey])

    useEffect(() => {
        if (!menuOpen) return
        const close = (event: PointerEvent) => {
            if (!menu.current?.contains(event.target as Node)) setMenuOpen(false)
        }
        document.addEventListener('pointerdown', close)
        return () => document.removeEventListener('pointerdown', close)
    }, [menuOpen])

    const choose = (key: string) => {
        setSelected(key)
        setVisited(previous => new Set([...previous, key]))
        setMenuOpen(false)
    }
    const visible = all.slice(0, capacity)
    const overflow = all.slice(capacity)
    const activeOverflow = overflow.some(tab => tab.key === active)
    const tabClass = 'shrink-0 px-4 py-3 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500'
    return (
        <div className="@container min-w-0 space-y-5" data-record-workspace>
            <header className="rounded-lg border border-neutral-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                <div className="p-5 sm:p-6">
                    {navigation && <div className="mb-4">{navigation}</div>}
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0 flex-1 basis-56">
                            {title && <p className="text-xs text-neutral-500 dark:text-slate-400 mb-2">{title}</p>}
                            {header}
                        </div>
                        {actions && <div className="flex shrink-0 items-center gap-2" aria-label="Record editing">{actions}</div>}
                    </div>
                    {toolbar && <div className="mt-5 border-t border-neutral-200 dark:border-slate-700 pt-3">{toolbar}</div>}
                </div>
                {tabs.length > 0 && <div className="px-2"><div ref={strip} className="relative min-w-0" data-record-tabs>
                    <div ref={measure} aria-hidden="true" className="absolute invisible pointer-events-none h-0 overflow-hidden flex w-max">
                        {all.map(tab => <span key={tab.key} className={tabClass}>{tab.label}</span>)}
                    </div>
                    <div className="flex min-w-0 items-end">
                        <div role="tablist" aria-label="Record sections" className="flex min-w-0">
                            {visible.map(tab => <button key={tab.key} type="button" role="tab" id={`${id}-${tab.key}-tab`}
                                data-guidance-fields={tab.guidanceFields?.join(' ')} data-guidance-company={guidanceCompany}
                                data-guidance-new={guidanceNew} data-guidance-open={tab.key === active}
                                aria-selected={tab.key === active} aria-controls={`${id}-${tab.key}-panel`}
                                tabIndex={tab.key === active || (activeOverflow && tab.key === 'general') ? 0 : -1}
                                onClick={() => choose(tab.key)}
                                onKeyDown={event => {
                                    const index = visible.findIndex(item => item.key === tab.key)
                                    const next = event.key === 'ArrowRight' ? (index + 1) % visible.length
                                        : event.key === 'ArrowLeft' ? (index + visible.length - 1) % visible.length
                                            : event.key === 'Home' ? 0 : event.key === 'End' ? visible.length - 1 : undefined
                                    if (next === undefined) return
                                    event.preventDefault()
                                    const target = visible[next]
                                    if (target) { choose(target.key); document.getElementById(`${id}-${target.key}-tab`)?.focus() }
                                }}
                                className={`${tabClass} ${tab.key === active ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'text-neutral-600 dark:text-slate-300 hover:bg-neutral-100 dark:hover:bg-slate-700 hover:text-neutral-900 dark:hover:text-slate-100'}`}>
                                {tab.label}
                            </button>)}
                        </div>
                        {overflow.length > 0 && <div ref={menu} className="relative ml-auto shrink-0 pb-2"
                            onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); setMenuOpen(false); more.current?.focus() } }}>
                            <button ref={more} type="button" aria-expanded={menuOpen} aria-controls={`${id}-more`} aria-label="More record sections"
                                className={`${V3_SECONDARY_BUTTON_CLS} inline-flex items-center gap-2 ${activeOverflow ? 'text-blue-600 dark:text-blue-400' : ''}`}
                                onClick={() => setMenuOpen(!menuOpen)}>More <Icon name="ChevronDown" size={14} /></button>
                            <div id={`${id}-more`} hidden={!menuOpen} className="absolute right-0 top-full z-30 mt-1 w-60 max-w-[80vw] rounded-lg border border-neutral-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg p-1">
                                {overflow.map(tab => <button key={tab.key} type="button" id={`${id}-${tab.key}-tab`}
                                    data-guidance-fields={tab.guidanceFields?.join(' ')} data-guidance-company={guidanceCompany}
                                    data-guidance-new={guidanceNew} data-guidance-open={tab.key === active}
                                    aria-controls={`${id}-${tab.key}-panel`} aria-current={tab.key === active ? 'page' : undefined}
                                    className="w-full rounded px-3 py-2 text-left text-sm text-neutral-700 dark:text-slate-200 hover:bg-neutral-50 dark:hover:bg-slate-700 focus-visible:ring-2 focus-visible:ring-blue-500"
                                    onClick={() => { choose(tab.key); more.current?.focus() }}>{tab.label}{tab.key === active && <span aria-hidden="true"> ✓</span>}</button>)}
                            </div>
                        </div>}
                    </div>
                </div></div>}
            </header>
            {all.map(tab => <div key={tab.key} id={`${id}-${tab.key}-panel`} role={tabs.length ? 'tabpanel' : undefined}
                aria-labelledby={tabs.length ? `${id}-${tab.key}-tab` : undefined} hidden={active !== tab.key}>
                {(visited.has(tab.key) || active === tab.key) && tab.content}
            </div>)}
        </div>
    )
}
